import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, type Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { IncidentRuleStatus } from '@api/devices';
import { FeatureFlagPipe } from '@core';
import { ScheduleControlComponent } from '@devices/components/schedule-control/schedule-control.component';
import { DeviceAttributeType, type DeviceData, type IncidentRuleBase, type IncidentRuleCommon } from '@devices/models';
import { IncidentSeverity } from '@models';
import { Store } from '@ngrx/store';
import { NotificationsService } from '@ui';
import { filter, take } from 'rxjs';

import { DevicesSelectors } from '../../../ngrx-store';
import { ConditionPickerComponent } from '../../condition-picker';
import { DatapointPickerComponent } from '../../datapoint-picker';
import { IncidentRuleConditionFieldsComponent } from '../../incident-rule-condition-fields';
import { OperatorComponent } from '../../operator';
import { IncidentRulesStore } from '../incident-rules.store';

interface Props {
  deviceId: string;
  incidentRule?: IncidentRuleCommon;
}

@Component({
  imports: [CommonModule, MatDialogModule, MatProgressSpinner, ReactiveFormsModule, DatapointPickerComponent, ConditionPickerComponent, OperatorComponent, MatFormFieldModule, MatInputModule, FeatureFlagPipe, ScheduleControlComponent, IncidentRuleConditionFieldsComponent],
  selector: 'avs-incident-rule-dialog',
  templateUrl: './incident-rule-dialog.html',
})
export class IncidentRuleDialogComponent {
  protected form: FormGroup;
  protected data = inject<Props>(MAT_DIALOG_DATA);
  protected realtimeAttributes = computed(() => {
    return this.device()?.additionalAttributes?.filter(
      attribute => attribute.attributeType === DeviceAttributeType.REALTIME,
    ) ?? [];
  });
  protected device: Signal<DeviceData | undefined>;
  protected durationMinutes: Signal<number>;
  protected incidentRulesStore = inject(IncidentRulesStore);

  private fb = inject(FormBuilder);
  private store = inject(Store);
  private notificationService = inject(NotificationsService);
  private dialogRef = inject(MatDialogRef);

  constructor() {
    this.form = this.fb.group({
      conditionValue: [{
        disabled: this.data.incidentRule?.system,
        value: this.data.incidentRule?.conditionValue || null,
      }, Validators.required],
      dataPoint: [{
        disabled: this.data.incidentRule?.system,
        value: this.data.incidentRule?.code || null,
      }, Validators.required],
      durationMinutes: [
        this.data.incidentRule ? Math.round(this.data.incidentRule.pendingPeriodSeconds / 60) : 5,
        [Validators.required, Validators.min(0), Validators.max(30)],
      ],
      name: [{
        disabled: this.data.incidentRule?.system,
        value: this.data.incidentRule?.name || null,
      }],
      operator: [{
        disabled: this.data.incidentRule?.system,
        value: this.data.incidentRule?.operator || null,
      }, Validators.required],
      schedule: [{
        config: this.data.incidentRule?.scheduleConfig,
        enabled: this.data.incidentRule?.scheduleEnabled || false,
      }],
      severity: [this.data.incidentRule?.severity || IncidentSeverity.MAJOR, Validators.required],
    });

    this.device = toSignal(
      this.store.select(DevicesSelectors.getDeviceById(this.data.deviceId)).pipe(
        filter(Boolean),
        take(1),
      ),
    );

    this.durationMinutes = toSignal(this.form.get('durationMinutes')!.valueChanges, {
      initialValue: this.form.get('durationMinutes')!.value,
    });
  }

  protected async submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();

      return;
    }
    const formValue = this.form.value;

    const reqBody = {
      ...(this.form.get('dataPoint')?.enabled && { code: formValue.dataPoint }),
      ...(this.form.get('conditionValue')?.enabled && { conditionValue: formValue.conditionValue }),
      ...(this.form.get('name')?.enabled && { name: formValue.name || null }),
      ...(this.form.get('operator')?.enabled && { operator: formValue.operator }),
      pendingPeriodSeconds: formValue.durationMinutes * 60,
      ...(this.form.get('schedule')?.enabled && {
        scheduleConfig: formValue.schedule.config,
        scheduleEnabled: formValue.schedule.enabled,
      }),
      ...(this.form.get('severity')?.enabled && { severity: formValue.severity }),
    };

    try {
      const incidentRule = this.data.incidentRule
        ? await this.incidentRulesStore.updateIncidentRule(this.data.incidentRule!.id, reqBody)
        : await this.incidentRulesStore.createIncidentRule({
          ...reqBody,
          status: IncidentRuleStatus.ACTIVE,
        } as IncidentRuleBase);

      if (incidentRule) {
        this.notificationService.showSuccessMessage(
          `Incident rule [${incidentRule.code}] was successfully ${this.data.incidentRule ? 'updated' : 'created'}`,
        );
        this.dialogRef.close();
      }
    } catch (e) {
      this.notificationService.showErrorMessage((e as HttpErrorResponse).message);
    }
  }


  protected async deleteIncidentRule() {
    try {
      const isDeleted = await this.incidentRulesStore.deleteIncidentRule(this.data.incidentRule!);

      if (isDeleted) {
        this.notificationService.showSuccessMessage(
          `Incident rule [${this.data.incidentRule?.code}] was successfully removed`,
        );
        this.dialogRef.close();
      }
    } catch (e) {
      this.notificationService.showErrorMessage((e as HttpErrorResponse).message);
    }
  }

}
